

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from './components/Layout';
import ResponsiveShadow from './components/ResponsiveShadow';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import { API_BASE_URL } from '../config/ApiConfig';
import useReferenceData from './components/hooks/useReferenceData';
import LoadingModal from './components/modals/LoadingModal';


function formatCurrency(value) {
  if (value == null) return '-';
  return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}


export default function SaldoAnterior() {
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const ano = localStorage.getItem('ano');
  const [saldoConta, setSaldoConta] = useState([]);
  const [saldoInvestimento, setSaldoInvestimento] = useState([]);
  const { contas, programas } = useReferenceData(token, cnpj);
  const [sortField, setSortField] = useState('idConta');
  const [sortDir, setSortDir] = useState('asc');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token || !cnpj || !ano) return;
    setIsLoading(true);
    setError(null);
    Promise.all([
      axios.get(`${API_BASE_URL.SALDO_CONTA}`, { params: { idEscola: cnpj, ano }, headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API_BASE_URL.SALDO_INVESTIMENTO}/search`, { params: { idEscola: cnpj, ano }, headers: { Authorization: `Bearer ${token}` } })
    ])
      .then(([contaRes, investimentoRes]) => {
        setSaldoConta(contaRes.data || []);
        setSaldoInvestimento(investimentoRes.data || []);
        // LOGS DE DEPURAÇÃO
        console.log('SaldoConta (ano atual):', contaRes.data);
        console.log('SaldoInvestimento (ano atual):', investimentoRes.data);
      })
      .catch(() => setError('Erro ao carregar dados de saldo.'))
      .finally(() => setIsLoading(false));
  }, [token, cnpj, ano]);

  // Agrupa por idConta: saldoConta.saldoFinal + saldoInvestimento.saldoInicial para cada conta
  // Garante que todas as contas de saldoInvestimento estejam presentes
  const contasMap = {};
  saldoInvestimento.forEach(i => {
    if (!i.idConta) return;
    if (!contasMap[i.idConta]) contasMap[i.idConta] = { saldoFinal: 0, saldoInicial: 0, programa: i.programa, idConta: i.idConta };
    contasMap[i.idConta].saldoInicial += Number(i.inicial ?? 0);
    if (i.programa) contasMap[i.idConta].programa = i.programa;
  });
  saldoConta.forEach(c => {
    if (!c.idConta) return;
    if (!contasMap[c.idConta]) contasMap[c.idConta] = { saldoFinal: 0, saldoInicial: 0, programa: c.programa, idConta: c.idConta };
    contasMap[c.idConta].saldoFinal += Number(c.saldoFinal ?? c.final ?? 0);
    if (c.programa) contasMap[c.idConta].programa = c.programa;
  });
  const contasArray = Object.values(contasMap).map(item => {
    const contaObj = (contas || []).find(c => c.idConta === item.idConta || c.numConta === item.idConta);
    // Tentar resolver idPrograma de várias formas: primeiro usar item.programa se for id, senão usar contaObj.idPrograma
    let idPrograma = '';
    if (item.programa) {
      // se item.programa corresponde a um id existente em programas, use-o
      const match = (programas || []).find(pp => String(pp.id) === String(item.programa) || String(pp.id) === String(Number(item.programa)));
      if (match) idPrograma = match.id;
    }
    if (!idPrograma && contaObj && contaObj.idPrograma) idPrograma = contaObj.idPrograma;

    const programaDesc = idPrograma ? ((programas || []).find(p => p.id === idPrograma) || {}).descricao || idPrograma : (typeof item.programa === 'string' ? item.programa : '');

    return {
      idConta: item.idConta,
      contaLabel: contaObj ? (contaObj.nomeConta || contaObj.numConta || contaObj.idConta) : item.idConta,
      programa: programaDesc,
      valor: item.saldoFinal + item.saldoInicial
    };
  });

  function getContaSortKey(item) {
    const s = item.contaLabel || item.idConta || '';
    const m = String(s).trim().match(/^(\d+)/);
    const num = m ? parseInt(m[1], 10) : NaN;
    const sufMatch = String(s).match(/-(.*)$/);
    const suffix = sufMatch ? sufMatch[1].toString().toUpperCase() : '';
    return { raw: String(s), num, suffix };
  }

  const sortedContasArray = React.useMemo(() => {
    if (!sortField) return contasArray;
    const copy = [...contasArray];
    copy.sort((a, b) => {
      if (sortField === 'idConta') {
        const ka = getContaSortKey(a);
        const kb = getContaSortKey(b);
        if (!isNaN(ka.num) && !isNaN(kb.num) && ka.num !== kb.num) return (ka.num - kb.num) * (sortDir === 'asc' ? 1 : -1);
        if (ka.num === kb.num) {
          const s = ka.suffix.localeCompare(kb.suffix, 'pt-BR', { sensitivity: 'base' });
          if (s !== 0) return s * (sortDir === 'asc' ? 1 : -1);
        }
        return ka.raw.localeCompare(kb.raw, 'pt-BR', { sensitivity: 'base' }) * (sortDir === 'asc' ? 1 : -1);
      }
      return 0;
    });
    return copy;
  }, [contasArray, sortField, sortDir]);

  const resumoPorProgramaMap = {};
  contasArray.forEach(item => {
    const nome = String(item.programa || '');
    if (!resumoPorProgramaMap[nome]) resumoPorProgramaMap[nome] = { nome, total: 0, qtd: 0 };
    resumoPorProgramaMap[nome].total += parseFloat(item.valor) || 0;
    resumoPorProgramaMap[nome].qtd += 1;
  });
  const resumoPorPrograma = Object.values(resumoPorProgramaMap);

  return (
    <Layout>
      <LoadingModal show={isLoading} />
      <ResponsiveShadow>
        <PageHeader title="SALDO ANTERIOR CONSOLIDADO" icon="bi-bank2" />
        <PageContent>
          {resumoPorPrograma.length > 0 && (
            <div className="mb-2 d-flex flex-wrap gap-2" style={{ maxHeight: '30vh', overflowY: 'auto', overflowX: 'hidden', flexShrink: 0 }}>
              {resumoPorPrograma.map(prog => (
                <div className="card border-0 shadow-sm" key={prog.nome || 'semprograma'} style={{ borderRadius: 10, flex: '1 1 0', minWidth: 200, maxWidth: '25%' }}>
                  <div className="card-body pt-2 pb-1 px-3">
                    <div className="d-flex justify-content-between align-items-center gap-2" style={{ backgroundColor: '#198754', color: '#fff', margin: '-0.5rem -1rem 0.5rem', padding: '0.15rem 1rem', borderRadius: '10px 10px 0 0', minHeight: '22px' }}>
                      <span className="fw-bold text-truncate" style={{ fontSize: '.62rem' }} title={prog.nome}>{prog.nome || 'Sem programa'}</span>
                      <span className="badge text-bg-light text-success flex-shrink-0" style={{ fontSize: '.62rem' }}>{prog.qtd}</span>
                    </div>
                    <div className="d-flex justify-content-between fw-bold" style={{ fontSize: '.78rem', whiteSpace: 'nowrap' }}>
                      <span>Total</span><span className="text-success">{formatCurrency(prog.total)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {isLoading ? (
            <div className="text-center py-3"><span className="spinner-border spinner-border-sm"></span></div>
          ) : error ? (
            <div className="alert alert-danger text-center my-4">{error}</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-sm w-100 mb-0" style={{ fontSize: '0.95rem' }}>
                <thead className="table-dark">
                  <tr>
                    <th>#</th>
                    <th>Programa</th>
                    <th>Conta</th>
                    <th className="text-end">Saldo Anterior</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedContasArray.map((item, idx) => (
                    <tr key={item.idConta}>
                      <td>{idx + 1}</td>
                      <td>{item.programa}</td>
                      <td>{item.contaLabel}</td>
                      <td className="text-end">{formatCurrency(item.valor)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {contasArray.length === 0 && (
                <div className="text-center text-muted py-3">Nenhum dado de saldo encontrado para o período selecionado.</div>
              )}
            </div>
          )}
        </PageContent>
      </ResponsiveShadow>
    </Layout>
  );
}
